import { useCallback, useEffect, useState } from 'react';
import {
  BookingFailure,
  fetchBooking,
  fetchSchedulingOffer,
} from './api/bookingOperations';
import { BookingDetails, BookingResultType } from './api/bookingApi.types';
import {
  BookingRefusalCode,
  isBookingTokenShaped,
} from './api/bookingTransport';
import { SchedulingPageState } from './scheduling.types';

/**
 * Turns a transport or contract failure into the state the page shows.
 * Only two refusals have their own screen; everything else is a generic
 * error with a retry, which is also where an unknown `__typename` lands.
 */
const failureState = (failure: BookingFailure): SchedulingPageState => {
  if (failure.kind === 'refused' && failure.code === BookingRefusalCode.NOT_FOUND) {
    return { kind: 'notFound' };
  }
  if (failure.kind === 'refused' && failure.code === BookingRefusalCode.TOO_MANY_REQUESTS) {
    return { kind: 'rateLimited' };
  }
  return { kind: 'error' };
};

/**
 * Whether a booked interview might have the hiring team's new times waiting
 * (a reschedule offer, BL-1599). The booking read gives the candidate no
 * actions while the request is anything but booked, and an open reschedule
 * offer is the one such state that still has a booking; any action on offer
 * proves the request is booked, so the offer read can be skipped.
 */
const mayHaveRescheduleOffer = (booking: BookingDetails): boolean =>
  !booking.canCancel && !booking.canReschedule;

/**
 * The hiring team's new times for an interview already booked, or the
 * booked page itself when there are none to choose from. Anything but an
 * open reschedule offer — expired, lapsed, no seats left, or a failed read —
 * shows the booking, which stands either way.
 */
const bookedOrRescheduleOffer = async (
  token: string,
  booking: BookingDetails
): Promise<SchedulingPageState> => {
  const booked: SchedulingPageState = { kind: 'booked', booking };
  if (!mayHaveRescheduleOffer(booking)) {
    return booked;
  }

  const offer = await fetchSchedulingOffer(token);
  if (
    !offer.ok ||
    offer.result.__typename !== BookingResultType.SCHEDULING_OFFER ||
    offer.result.currentInterview === null
  ) {
    return booked;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { __typename, ...payload } = offer.result;
  return { kind: 'ready', offer: payload };
};

/**
 * The read flow from the integration guide: ask for the booking first, and
 * only if nothing is booked ask for the offer. A candidate who already booked
 * must see their interview even after the link has expired, which is why the
 * booking is read first and on its own. A booking with a reschedule offer
 * open on it reads the offer too, so the candidate can answer it.
 */
export const loadSchedulingPage = async (
  token: string | undefined
): Promise<SchedulingPageState> => {
  // A token that cannot bind is refused by the API without a lookup; skipping
  // the round trip here gives the same answer sooner.
  if (!isBookingTokenShaped(token) || token === undefined) {
    return { kind: 'notFound' };
  }

  const booking = await fetchBooking(token);
  if (!booking.ok) {
    return failureState(booking.failure);
  }
  if (booking.result.__typename === BookingResultType.BOOKING_DETAILS) {
    return bookedOrRescheduleOffer(token, booking.result);
  }
  if (booking.result.__typename === BookingResultType.LINK_REPLACED_STATE) {
    return { kind: 'replaced' };
  }

  const offer = await fetchSchedulingOffer(token);
  if (!offer.ok) {
    return failureState(offer.failure);
  }
  switch (offer.result.__typename) {
    case BookingResultType.SCHEDULING_OFFER: {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { __typename, ...payload } = offer.result;
      return { kind: 'ready', offer: payload };
    }
    case BookingResultType.EXPIRED_LINK_STATE:
      return { kind: 'expired', expiredAt: offer.result.expiredAt };
    case BookingResultType.LINK_REPLACED_STATE:
      return { kind: 'replaced' };
    case BookingResultType.NO_SLOTS_AVAILABLE_STATE:
      return { kind: 'noSlots' };
    case BookingResultType.OFFER_CLOSED_STATE:
      return { kind: 'closed' };
    case BookingResultType.DECLINE_RECORDED:
      return { kind: 'declined' };
    default:
      return { kind: 'error' };
  }
};

/**
 * Loads what the candidate's link resolves to.
 *
 * Deliberately not the shared candidate-client Apollo client: its auth link
 * attaches session tokens and its error link refreshes on 401, neither of
 * which applies to a candidate who is never logged in. See `api/`.
 *
 * `reload` re-runs the read flow, for the retry button and for refreshing
 * the offer after a seat is taken by somebody else.
 */
export const usePublicSchedulingOffer = (
  token: string | undefined
): {
  state: SchedulingPageState;
  reload: () => void;
} => {
  const [state, setState] = useState<SchedulingPageState>({
    kind: 'loading',
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    loadSchedulingPage(token)
      .catch((): SchedulingPageState => ({ kind: 'error' }))
      .then(next => {
        if (!cancelled) {
          setState(next);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, attempt]);

  const reload = useCallback(() => setAttempt(n => n + 1), []);

  return { state, reload };
};
